import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import Business from './business/Business';
import ClientProfile from './profile/client/ClientProfile';
import FamilyProfile from './profile/family/FamilyProfile';
import MyselfProfile from './profile/myself/MyselfProfile';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import api from 'react/signups/card/api.js';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';
import { booleanToYesNo, convertDate, yesNoToBoolean } from 'react/signups/card/utils';

export default function Admin({
  cardAccountSignup,
  organization,
  recipe,
  setCardAccountSignup,
  setUser,
  setOrganization,
  user,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // State hooks ------------------------------------------------

  const [moduleComplete, setModuleComplete] = useState(false);

  const [profileData, setProfileData] = useState({
    address: {
      street1: (userType !== USER_TYPES.CLIENT && organization.address?.street1) || '',
      street2: (userType !== USER_TYPES.CLIENT && organization.address?.street2) || '',
      city: (userType !== USER_TYPES.CLIENT && organization.address?.city) || '',
      state: (userType !== USER_TYPES.CLIENT && organization.address?.state) || '',
      zip: (userType !== USER_TYPES.CLIENT && organization.address?.zip) || '',
    },
    dob: convertDate(user.dob) || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    mobile: user.mobile || '',
    referralCategory: cardAccountSignup.referralCategory || '',
    referralCategoryDetail: cardAccountSignup.referralCategoryDetail || '',
    referralName: cardAccountSignup.referralName || '',
    countryOfCitizenship: user?.countryOfCitizenship || '',
    nonResidentAlien: booleanToYesNo(user?.nonResidentAlien) || '',
  });

  const [businessData, setBusinessData] = useState({
    name: organization.name || '',
    address: {
      street1: (userType === USER_TYPES.CLIENT && organization.address?.street1) || '',
      street2: (userType === USER_TYPES.CLIENT && organization.address?.street2) || '',
      city: (userType === USER_TYPES.CLIENT && organization.address?.city) || '',
      state: (userType === USER_TYPES.CLIENT && organization.address?.state) || '',
      zip: (userType === USER_TYPES.CLIENT && organization.address?.zip) || '',
    },
    numberOfClients: cardAccountSignup.numberOfClients || '',
    numberOfInitialCards: cardAccountSignup.numberOfInitialCards || '',
    cardForPaidCaregiver: cardAccountSignup.cardForPaidCaregiver || '',
  });

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      recipe,
      moduleComplete,
      setModuleComplete,
      userType,
    });
  }, [
    cardAccountSignup.id,
    location,
    navigate,
    recipe,
    userType,
    moduleComplete,
    setModuleComplete,
  ]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete) return;

    let address = profileData.address;
    if (!address || address.street1 === '') {
      address = businessData.address;
    }

    const params = {
      cardAccountSignup: {
        numberOfClients: businessData.numberOfClients,
        numberOfInitialCards: businessData.numberOfInitialCards,
        referralCategory: profileData.referralCategory || '',
        referralCategoryDetail: profileData.referralCategoryDetail || '',
        referralName: profileData.referralName || '',
        cardForPaidCaregiver: businessData.cardForPaidCaregiver,
      },
      user: {
        countryOfCitizenship: profileData.countryOfCitizenship,
        nonResidentAlien: yesNoToBoolean(profileData.nonResidentAlien),
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        dob: profileData.dob,
        mobile: profileData.mobile,
        addressesAttributes: [{ ...address, address_type: 'home' }],
      },
      organization: {
        name: businessData.name,
        addressesAttributes: [{ ...address, address_type: 'business' }],
      },
    };

    api
      .updateUser(user.id, params)
      .then((response) => {
        const signup = response.data.card_account_signup.data;
        const organization = response.data.organization.data;
        const userData = response.data.user.data;

        setCardAccountSignup({
          id: signup.id,
          relationships: signup.relationships,
          ...signup.attributes,
        });
        setOrganization({
          id: organization.id,
          ...organization.attributes,
          address: {
            id: organization.relationships.address.data.id,
            ...organization.relationships.address.data.attributes,
          },
        });
        setUser({ id: userData.id, ...userData.attributes });
      })
      .catch(() => {
        setModuleComplete(false);
      });
  }, [moduleComplete]);

  // navigate to next step when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    onNavigate();
  }, [profileData, businessData]);

  // Navigate to next module when Application-level state changes in response to api success
  useUpdateEffect(() => {
    onNavigate();
  }, [user]);

  // Pass-down state, props and handlers ------------------------------

  const passDownValues = {
    firstName: cardAccountSignup.cardholderFirstName || '',
    businessData,
    profileData,
    moduleComplete,
  };

  const passDownHandlers = {
    setBusinessData,
    setProfileData,
  };

  const profile = (
    <>
      {userType === USER_TYPES.CLIENT && (
        <ClientProfile {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.FAMILY && (
        <FamilyProfile {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.MYSELF && (
        <MyselfProfile {...passDownValues} {...passDownHandlers} />
      )}
    </>
  );

  // This catches any hard refresh within this module and starts the user over
  if (!useMatch('/admin/profile') && !profileData.firstName) {
    return <Navigate replace to={recipe.defaultPath} />;
  }

  return (
    <Routes>
      <Route element={profile} path="/profile" />
      <Route element={<Business {...passDownValues} {...passDownHandlers} />} path="/business" />
      <Route element={<Navigate replace to={recipe.defaultPath} />} path="/*" />
    </Routes>
  );
}

Admin.propTypes = {
  cardAccountSignup: cardAccountSignupShape,
  organization: organizationShape.isRequired,
  recipe: recipeShape.isRequired,
  setCardAccountSignup: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  setOrganization: PropTypes.func.isRequired,
  user: userShape.isRequired,
};
